import { useState } from "react";
import Input from "../../components/form/input/InputField";
import Label from "../../components/form/Label";
import { Link } from "react-router-dom"; // ✅ Fixed import path
import { EyeCloseIcon, EyeIcon } from "../../icons";
import Checkbox from "../../components/form/input/Checkbox";
import Button from "../../components/ui/button/Button";
import PageMeta from "../../components/common/PageMeta";
import axiosInstance from "../../hooks/axios";

export default function SignIn() {
  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  // Email validation function
  const validateEmail = (email: string) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  // Handle form submission
  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    let validationErrors: { email?: string; password?: string } = {};

    // Validate email
    if (!email.trim()) {
      validationErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      validationErrors.email = "Invalid email format";
    }

    // Validate password
    if (!password.trim()) {
      validationErrors.password = "Password is required";
    } else if (password.length < 2) {
      validationErrors.password = "Password must be at least 6 characters";
    }

    // If errors exist, set state and stop submission
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    setErrors({});

    // Perform API login request with Axios
    try {
      const response = await axiosInstance.post("/auth/login", { email, password });
      
      if (response.data && response.data.token && response.data.userId) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("userId", response.data.userId);
        if (response.data.refreshToken) {
          localStorage.setItem("refreshToken", response.data.refreshToken);
        }
        if (response.data.sessionId) {
          localStorage.setItem("sessionId", response.data.sessionId);
        }
        window.location.href = "/";
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Invalid credentials");
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta title="Sign In" description="Sign in to access the Taxi Admin operations dashboard." />

      <div className="relative flex w-full h-screen px-4 py-6 overflow-hidden bg-white z-1 dark:bg-gray-900 sm:p-0">
        <div className="flex flex-col flex-1 p-6 rounded-2xl sm:rounded-none sm:border-0 sm:p-8">
         
          <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
            <div>
              <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                Sign In
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Enter your email and password to sign in!</p>

              {/* Login Form */}
              <form onSubmit={handleLogin}>
                <div className="space-y-6">
                  {/* Email Field */}
                  <div>
                    <Label>Email <span className="text-error-500">*</span></Label>
                    <Input
                      placeholder="info@gmail.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                  </div>

                  {/* Password Field */}
                  <div>
                    <Label>Password <span className="text-error-500">*</span></Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                      >
                        {showPassword ? <EyeIcon className="fill-gray-500 dark:fill-gray-400" /> : <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />}
                      </span>
                    </div>
                    {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
                  </div>

                  {/* Remember Me & Forgot Password */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={isChecked} onChange={() => setIsChecked(!isChecked)} />
                      <span className="block font-normal text-gray-700 text-theme-sm dark:text-gray-400">
                        Keep me logged in
                      </span>
                    </div>
                    <Link to="/forgot-password" className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400">
                      Forgot password?
                    </Link>
                  </div>

                  {/* Sign In Button */}
                  <div>
                    <Button className="w-full" size="sm" type="submit" disabled={loading}>
                      {loading ? "Signing in..." : "Sign in"}
                    </Button>
                  </div>

                  {/* Sign Up */}
                  <div className="text-center">
                    <p className="text-sm font-normal text-gray-700 dark:text-gray-400">Don't have an account?<span> </span>
                    <Link to="/signup" className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400">
                      Sign Up
                    </Link></p>
                  </div>
                </div>

                {/* Error Message */}
                {error && <p className="mt-3 text-red-500 text-sm">{error}</p>}
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
